import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { type RejectionChoice } from "./AskV2ReviewRejectScopePrompt";
import { type CommentWithOptionalTags } from "./reviewCommentTags";
import { useUpdateAskPunttReviewComment } from "./useAskPunttReview";
import { panelNumberOf } from "./useAskV2ReviewCommentContext";
import { type useAskV2ReviewWorkspace } from "./useAskV2ReviewWorkspace";

import { useAnalytics } from "../../utils/analytics";
import { reviewCommentGroup } from "../../utils/visibleReviewComments";
import { type AskV2CommentContext } from "../transport/types";
import { type AskV2TransportApi } from "../transport/useAskV2Transport";

type UseAskV2ReviewJudgeArgs = {
  reviewId: ReturnType<typeof useAskV2ReviewWorkspace>["reviewId"];
  panelComments: readonly CommentWithOptionalTags[];
  selectComment: (commentId: string) => void;
  clearComment: () => void;
  /** The chip a message about one finding carries; null when the review does not hold it. */
  commentContextFor: (commentId: string) => AskV2CommentContext | null;
  transport: Pick<
    AskV2TransportApi,
    "send" | "sendWhenIdle" | "setCommentContext" | "removeCommentContext"
  >;
};

/**
 * Confirm, reject and restore on a finding: writes the verdict, then moves the
 * selection on to the next open finding. A rejection she makes an enterprise
 * rule also sends her reason to the chat, tied to the finding (warhol spec
 * surbhi.kapoor/0100).
 */
export function useAskV2ReviewJudge({
  reviewId,
  panelComments,
  selectComment,
  clearComment,
  commentContextFor,
  transport,
}: UseAskV2ReviewJudgeArgs) {
  const { t } = useTranslation("ui");
  const [judgmentFailed, setJudgmentFailed] = useState(false);
  const { mutateAsync: updateReviewComment, isPending: isJudgmentPending } =
    useUpdateAskPunttReviewComment(reviewId);
  const analytics = useAnalytics();

  const handleJudgeComment = useCallback(
    async (
      commentId: string,
      liked: boolean | null,
      rejection?: RejectionChoice,
    ) => {
      const visibleList = panelComments;
      const index = visibleList.findIndex((item) => item._id === commentId);
      const judged = visibleList.find((item) => item._id === commentId);
      const previousLiked = judged?.liked ?? null;
      // why: `liked: null` arrives from Restore on a rejected finding AND from
      // a second Confirm press that clears a confirmation; only the first is a
      // restore. Read off the section, because a past review's rejection
      // leaves `liked` null.
      const isRestore =
        // Stryker disable next-line ConditionalExpression: isRestore is read only in the liked === null branch, so forcing this term true changes nothing; forcing it false is killed by "records Restore on a rejected finding as a restore"
        liked === null &&
        judged != null &&
        reviewCommentGroup(judged) === "rejected";

      analytics.capture("ask_puntt_feedback", {
        feedback:
          liked === true
            ? "comment_thumbs_up"
            : liked === false
              ? "comment_thumbs_down"
              : isRestore
                ? "comment_restore"
                : "comment_unconfirm",
        comment_id: commentId,
        review_id: reviewId,
      });

      // The reach and her reason ride with the verdict rather than in a
      // second call, so the ledger row warhol reads is whole when it lands.
      setJudgmentFailed(false);
      try {
        await updateReviewComment({
          commentId,
          body:
            liked === false && rejection
              ? {
                  liked,
                  judgmentReach: rejection.reach,
                  ...(rejection.reason
                    ? { judgmentReason: rejection.reason }
                    : {}),
                }
              : { liked },
        });
      } catch {
        // why: stay on the finding and say so. Nothing was written and the
        // card keeps its prior state; advancing would read as saved, and the
        // reject prompt has already closed, so without this line a failed
        // save is invisible.
        setJudgmentFailed(true);
        return;
      }

      // Stryker disable next-line ConditionalExpression: a null verdict matches neither branch below, so skipping this return changes nothing; forcing it true is killed by "moves on to the next finding after a confirm"
      if (liked === null) return;

      if (liked === true && previousLiked !== true) {
        const next = visibleList[index + 1];
        if (next) selectComment(next._id);
        return;
      }

      if (liked === false && rejection?.reach === "rule") {
        // why before moving on: the chip must name THIS finding, and moving
        // selects the next one. Warhol reads the rule from the ledger row the
        // write above landed, and draws the card she confirms in the chat.
        // why the fallback: the ledger already says she asked for a rule, so
        // the request must reach the chat even for a finding the review's
        // comments do not hold yet; warhol reads only its review and comment id
        const context =
          commentContextFor(commentId) ??
          (reviewId
            ? {
                id: `review-comment-${commentId}`,
                commentId,
                reviewId,
                number: panelNumberOf(panelComments, commentId),
                title: "",
              }
            : null);
        if (context) {
          const message = t("askV2.review.ruleRequestMessage", {
            reason: rejection.reason,
          });
          transport.setCommentContext(context);
          void transport.send({ message }).then((sent) => {
            // why: send refuses while a turn runs, which is when the live
            // findings show this prompt; the chip comes off so her next
            // message does not carry a request she never typed, and the page's
            // transport holds the request until the turn ends, even if this
            // pane closes
            if (!sent) {
              transport.removeCommentContext(context.id);
              transport.sendWhenIdle({ message, commentContext: context });
            }
          });
        }
      }

      if (liked === false) {
        // why: the rejected card stays in sight, grayed, under Rejected, so
        // moving on never hides it. Skip findings already rejected, here or in a past review
        // (not restored): landing on one would open a decision already made.
        const isOpen = (item: (typeof visibleList)[number]) =>
          // Stryker disable next-line ConditionalExpression: both slices already leave out the judged finding, so forcing the id check true changes nothing; forcing either check false, or the rejected check true, is killed by "skips rejected findings after a reject and sends the scope with the verdict"
          item._id !== commentId && reviewCommentGroup(item) !== "rejected";
        const next =
          visibleList.slice(index + 1).find(isOpen) ??
          visibleList.slice(0, Math.max(index, 0)).reverse().find(isOpen);
        if (next != null) {
          selectComment(next._id);
        } else {
          clearComment();
        }
      }
    },
    [
      analytics,
      clearComment,
      commentContextFor,
      panelComments,
      reviewId,
      selectComment,
      t,
      transport,
      updateReviewComment,
    ],
  );

  return { judgmentFailed, isJudgmentPending, handleJudgeComment };
}
